import { Component, Prop, h, State, Element, Host, Event, type EventEmitter, Method } from '@stencil/core';
import { type Option, DisplayType, type Detail, OptionConfig } from './interfaces';
import CheckCircleIcon from '../../assets/svg/check.svg';
import CameraIcon from '../../assets/svg/camera.svg';
import FileIcon from '../../assets/svg/file-upload.svg';

@Component({
  tag: 'salla-product-options',
  styleUrl: 'salla-product-options.scss',
})
export class SallaProductOptions {

  constructor() {
    this.canDisabled = !salla.config.get('store.settings.product.notify_options_availability');
    salla.lang.onLoaded(() => {
      this.outOfStockText = salla.lang.get("pages.products.out_of_stock");
      this.donationAmount = salla.lang.get('pages.products.donation_amount');
      this.selectDonationAmount = salla.lang.getWithDefault('pages.products.select_donation_amount', 'تحديد مبلغ التبرع');
      this.selectAmount = salla.lang.getWithDefault('pages.products.select_amount', 'اختر المبلغ');
    });

    if (this.options) {
      try {
        this.setOptionsData(Array.isArray(this.options) ? this.options : JSON.parse(this.options));
        return;
      } catch (e) {
        salla.log('Bad json passed via options prop');
      }
    }
    if (!Array.isArray(this.optionsData)) {
      salla.log('Options is not an array[] ---> ', this.optionsData);
      this.setOptionsData([]);
    }

    if (this.productId && !salla.url.is_page('cart')) {
      salla.api.product.getDetails(this.productId, ['options']).then(resp => this.setOptionsData(resp.data.options));
    }
  }
  /**
   * Sets the options data for the product
   * @param optionsData - Array of product options
   */
  @Method()
  async setOptionsData(optionsData: Option[]) {
    this.optionsData = optionsData;
    const that = this
    this.optionsData[0]?.details?.forEach(function (detail) {
      Object.entries(detail.skus_availability || {})
        .filter(sku => !sku[1])
        .map(sku => that.outSkus.push(Number(sku[0])));
    });
  }

  @Element() host: HTMLElement;

  private fileTypes: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    word: 'application/doc,application/ms-doc,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    exl: 'application/excel,application/vnd.ms-excel,application/x-excel,application/x-msexcel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    txt: 'text/plain',
  };

  @State() optionsData: Option[]
  @State() outOfStockText = ""
  @State() donationAmount: string = salla.lang.get('pages.products.donation_amount')
  @State() selectDonationAmount: string = salla.lang.getWithDefault('pages.products.select_donation_amount', 'تحديد مبلغ التبرع')
  @State() selectAmount: string = salla.lang.getWithDefault('pages.products.select_amount', 'اختر المبلغ')
  @State() isCustomDonation = false;
  @State() selectedOptions = [];
  @State() canDisabled: boolean;
  @State() selectedSkus?: Array<string | number>;
  @State() selectedOutSkus?: Array<string | number>;
  @State() disableCardValue = true
  @State() availableDigitalCardValues: Detail[] = []

  private outSkus: Array<string | number> = [];
  private donationInput?: HTMLInputElement;
  private optionConfig: OptionConfig;
  /**
 * Avoid selection of previous default or selected card value option 
 * when switching between digital card country options for the 1st time
 */
  private ignoreDefaultCardValue = false
  /**
   * The id of the product to which the options are going to be fetched for.
   */
  @Prop() productId: number = salla.config.get('page.id');

  /**
   * Product detail information.
   */
  @Prop() options: string

  /**
   * Hide the out of stock label.
   * Will be used in the case of live validation such as cart page and inline checkout
   */
  @Prop() hideOutLabel: string

  /**
   * A unique key used to generate distinct identifiers.
   * 
   * This key is appended to a default value to ensure uniqueness
   * across instances or components where differentiation is required.
   */
  @Prop() uniqueKey: string

  /**
   * Configuration for customizing the display layout of product options.
   * The `config` prop accepts a JSON string that specifies the layout type for rendering options.
   * It allows customization of how single-option and multiple-option selections are displayed.
   * 
   * Example Usage:
   * ```html
   * <salla-product-options 
   *   options="{{ product.options }}" 
   *   product-id="{{ product.id }}" 
   *   unique-key="abc123"
   *   config='{
   *     "single-option": { "type": "button" },
   *     "multiple-option": { "type": "button" }
   *   }'>
   * </salla-product-options>
   * ```
   * 
   * Example Config JSON:
   * ```json
   * {
   *   "single-option": { "type": "button" },
   *   "multiple-option": { "type": "default" }
   * }
   * ```
   * 
   * - `single-option`:
   *   - Defines the display style for single-option selections.
   *   - `type`: Supported values:
   *     - `"button"`: Displays options in a button-style grid layout.
   *     - `"default"`: Displays options as a dropdown (default).
   * 
   * - `multiple-option`:
   *   - Defines the display style for multiple-option selections.
   *   - `type`: Supported values:
   *     - `"button"`: Displays options in a button-style grid layout.
   *     - `"default"`: Displays options as a list of checkboxes (default).
   */
  @Prop() config: string;

  /**
   * Get the id's of the selected options.
   * */
  @Method()
  async getSelectedOptionsData() {
    const selectedOptions = {}
    const formData = (this.host as HTMLElement & { getElementSallaData: () => FormData }).getElementSallaData();
    formData.forEach((value, key) => {
      if (key.startsWith('options[')) { (selectedOptions[key.replace('options[', '').replace(']', '')] = value) }
    });
    return selectedOptions;
  }

  /**
   * Report options form validity.
   * */
  @Method()
  async reportValidity() {
    const requiredElements = this.host.querySelectorAll('[required]') as unknown as Array<HTMLInputElement>;
    let pass = true;
    for (let i = 0; i < requiredElements.length; i++) {
      //if there is only one invalid option, return false
      if ('reportValidity' in requiredElements[i] && !requiredElements[i].reportValidity()) {
        pass = false;
      }
    }
    return pass;
  }

  /**
   * Return true if there is any out of stock options are selected and vise versa.
   * */
  @Method()
  async hasOutOfStockOption() {
    return this.selectedOptions.some(option => option.is_out) || (this.selectedSkus?.length && this.selectedSkus?.every(sku => this.outSkus.includes(sku)));
  }

  /**
   * Get selected options.
   * */
  @Method()
  async getSelectedOptions() {
    return this.selectedOptions;
  }

  /**
   * Get a specific option by its id.
   * */
  @Method()
  async getOption(option_id) {
    return this.optionsData.find(option => option.id === option_id);
  }

  /**
   * An event that emitted when any option is changed.
   */
  @Event() changed: EventEmitter;

  // @ts-ignore
  private invalidHandler(event: Event, option: Option) {
    const closestProductOption = (event.target as HTMLInputElement).closest('.s-product-options-option') as HTMLElement;

    if (!closestProductOption.classList.contains('s-product-options-option-error')) {
      closestProductOption.classList.add('s-product-options-option-error');
    }

    if (!salla.url.is_page('cart')) {
      const firstInvalidElement = this.host.querySelector('.s-product-options-option-error');
      if (firstInvalidElement === closestProductOption) {
        this.scrollToElement(closestProductOption);
      }
    }
  }

  private scrollToElement(element: HTMLElement) {
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private changedHandler(event, option, fireChangeEvent = true) {
    const data = {
      event: event,
      option: option,
      detail: null,
      productId: this.productId
    };

    if (option.details) {
      const detail = option.details.find((detail) => {
        return Number(detail.id) === Number(event.target.value);
      });
      data.detail = detail;
    }

    if (option.type === 'country') {
      this.handleCountryOptionChange(event, data.detail);
    }

    const optionElement = event.target.closest('.s-product-options-option');
    if (event.target.value ||
      ((option.type === DisplayType.FILE || option.type === DisplayType.IMAGE) && event.type === 'added') ||
      (option.type === DisplayType.MAP && event.type === 'selected' && (event.target.lat && event.target.lng))) {
      setTimeout(() => {
        optionElement.classList.remove('s-product-options-option-error');
      }, 200);
    }

    if (option.type === DisplayType.DONATION) {
      salla.event.emit('product-options::donation-changed', {
        id: this.productId,
        price: event.target.value
      });
    }

    this.setSelectedSkus();
    this.handleRequiredMultipleOptions(option);

    const index = this.selectedOptions.findIndex(opt => opt.option_id === data.option.id);

    if (data.option.type === DisplayType.MULTIPLE_OPTIONS) {
      // Handle multiple selections
      const detailIndex = this.selectedOptions.findIndex(
        opt => opt.option_id === data.option.id && opt?.id === data.detail?.id
      );
      if (detailIndex > -1) {
        // If the option is already selected, remove it (unselect)
        this.selectedOptions.splice(detailIndex, 1);
      } else {
        // If the option is not selected, add it to the selectedOptions array
        this.selectedOptions.push({ ...data.detail, option_id: data.option.id });
      }
    } else {
      // Handle single selection
      if (!data.detail || Object.keys(data.detail).length === 0) {
        // If there is no value for the single-select, remove it from the selectedOptions array
        if (index > -1) {
          this.selectedOptions.splice(index, 1);
        }
      } else {
        // If a value exists, update or add the selection
        if (index > -1) {
          // Replace the existing selection with the new one
          this.selectedOptions[index] = { ...data.detail, option_id: data.option.id };
        } else {
          // If no selection exists for this input, add the new selection
          this.selectedOptions.push({ ...data.detail, option_id: data.option.id });
        }
      }
    }

    
    // Update optionsData directly
    this.optionsData = this.optionsData.map(opt => {
      if (opt.id === data.option.id) {
        return {
          ...opt,
          details: opt.details.map(detail => ({
            ...detail,
            is_selected: data.option.type === DisplayType.MULTIPLE_OPTIONS
              ? this.selectedOptions.some(selected => selected.id === detail.id)
              : Number(detail.id) === Number(data.detail?.id),
            value: data.detail?.value
          }))
        };
      }
      return opt;
    });
    // Emit the event only if fireChangeEvent is true
    if (fireChangeEvent) {
      this.changed.emit(data);
      salla.event.emit('product-options::change', data);
    }
  }


  private handleDonationOptions = (event, detail, type) => {
    if (detail === 'custom' && type === 'input') {
      salla.helpers.inputDigitsOnly(event.target)
      salla.event.emit('product-options::donation-changed', {
        id: this.productId,
        price: event.target.value
      });
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.isCustomDonation = event.target.value === 'custom';
    if (this.donationInput) {
      if (event.target.value === 'custom') {
        this.donationInput.value = '';
        this.donationInput.focus()
      } else {
        this.donationInput.value = event.target.value;
      }
      if (detail === 'custom') {
        return
      }
      salla.event.emit('product-options::donation-changed', {
        id: this.productId,
        price: event.target.value
      });
    }
  }

  private hideLabel = (option) => {
    if (option.type === DisplayType.DONATION && (option.donation && !option.donation.can_donate)) {
      return true;
    }
    return false;
  }

  private getExpireDonationMessage = (option) => {
    if (!option.donation) {
      return;
    }
    const completed = option.donation.target_amount <= option.donation.collected_amount;
    return <div class={{ "s-product-options-donation-message": true, "s-product-options-donation-completed": completed, "s-product-options-donation-expired": !completed }}>
      <p>{option.donation.target_message}</p>
      <span innerHTML={completed ? salla.money(option.donation.target_amount) : ''}></span>
    </div>
  }
  /**
   * loop throw all selected details, then get common sku, if it's only one, means we selected all of them;
   */
  private setSelectedSkus() {
    this.selectedSkus = this.selectedOptions.map(detail => Object.keys(detail.skus_availability || {}))
      .reduce((p, c) => p.filter(e => c.includes(e)), []) // Initialize accumulator as an empty array
      .map(sku => Number(sku));
  }
  

  private handleRequiredMultipleOptions(option) {
    if (option.type !== DisplayType.MULTIPLE_OPTIONS || !option.required) {
      return;
    }
    const optionContainer = this.host.querySelector(`[data-option-id="${option.id}"]`);
    const hasChecked = optionContainer.querySelectorAll('input:checked').length;
    optionContainer.querySelectorAll('input').forEach(input => input.toggleAttribute('required', !hasChecked));
  }

  private getLatLng(value, type: 'lat' | 'lng') {
    return value
      ? value.split(',')[type === 'lat' ? 0 : 1]
      : '';
  }

  private getDisplayForType(option: Option) {
    if (this[`${option.type}Option`]) {
      return this[`${option.type}Option`](option);
    }

    if (option.type === DisplayType.COLOR_PICKER) {
      return this.colorPickerOption(option)
    }

    if (option.type === DisplayType.MULTIPLE_OPTIONS) {
      return this.multipleOptions(option);
    }

    if (option.type === DisplayType.SINGLE_OPTION) {
      return this.singleOption(option);
    }

    if (option.type === DisplayType.DIGITAL_CARD_VALUE) {
      return this.digitalCardValuesOption(option);
    }
    if (option.type === DisplayType.COUNTRY) {
      return this.countryOption(option);
    }

    if (option.type === DisplayType.BOOKING && salla.url.is_page("cart")) {
      return <salla-booking-field onInvalidInput={(e) => this.invalidHandler(e, option)} option={option as any} productId={option.value}></salla-booking-field>
    }

    salla.log(`Couldn't find options type(${option.type})😢`);
    return '';
  }

  protected getOptionShownWhen(option: Option) {
    return option.visibility_condition
      ? { "data-show-when": `options[${option.visibility_condition.option}] ${option.visibility_condition.operator} ${option.visibility_condition.value}` }
      : {};
  }

  private getAvailableDigitalCardSKUs(detail: Detail) {
    const digitalCardOption = this.optionsData.find(({ type }) => type === 'digital-code-value')

    if (!digitalCardOption) throw new Error('product-options:: No digital card options found')

    const outofStockSKUs = Object.keys(detail.skus_availability).filter(key => detail.skus_availability[key] === false)

    this.availableDigitalCardValues = digitalCardOption.details.filter((op) => {

      return !Object.keys(op.skus_availability).filter(SKU_key => outofStockSKUs.includes(SKU_key)).length

    })

  }
  private handleCountryOptionChange(event: Event, detail: Detail) {
    event.stopImmediatePropagation()

    this.ignoreDefaultCardValue = true

    const currentCardValue = this.host.querySelector("input[data-code-value]:checked") as HTMLInputElement | null

    if (currentCardValue) currentCardValue.checked = false

    const digitalCardOption = this.optionsData.find(({ type }) => type === 'digital-code-value')

    if (!digitalCardOption) throw new Error('product-options:: No digital card options found')

    this.getAvailableDigitalCardSKUs(detail)
  }

  private getSelectedDigitalCardOptions(option: Option) {
    const selectedOption = option.details.find(detail => detail.is_selected)
    const defaultOption = option.details.find(detail => !!detail.is_default) || option.details[0] /*option.details[0] only applys for counrty options*/
    if (!['digital-code-value', 'country'].includes(option.type)) return

    return selectedOption || defaultOption


  }

  //we need the cart Id for productOption Image
  componentWillLoad() {
    if(salla.url.is_page("cart")){
      this.disableCardValue=false
      this.fillSelectedOptions();
    }

  if (this.config) {
      try {
        this.optionConfig = typeof this.config === 'string' ? JSON.parse(this.config) : this.config;
      } catch (error) {
        console.error('Failed to parse JSON in config prop:', error);
      }
    }

    const shouldSelectDefaultOption = this.optionsData.filter(({ type }) => ["country", "digital-card-value"].includes(type)).length > 0 && salla.url.is_page('cart')

    if (shouldSelectDefaultOption) {
      const countryOption = this.optionsData.find(option => option.type === 'country')

      const defaultSelection = countryOption && this.getSelectedDigitalCardOptions(countryOption)

      if (defaultSelection) {
        this.getAvailableDigitalCardSKUs(defaultSelection)
      }
    }
    this.outOfStockText = salla.lang.get('pages.products.out_of_stock')
    return salla.onReady(() => {
      const needsCartId = (!salla.storage.get('cart.id') && this.optionsData.some(option => ['file', 'image'].includes(option.type)));
      return needsCartId ? salla.api.cart.getCurrentCartId(false, "salla-product-options") : null;
    })
  }

  private hideDigitalCardsOptions(option:Option){
    return (this.disableCardValue && option.type===DisplayType.DIGITAL_CARD_VALUE && !salla.url.is_page("cart"))
   }

  render() {
    if (this.optionsData?.length === 0) {
      return;
    }

    return (
      <Host class="s-product-options-wrapper">
        {/* TODO:: move salla-conditional-field logic to here, no need of another component*/}
        <salla-conditional-fields>
          {this.optionsData.map((option: Option) =>
            <div key={option.id} class={`s-product-options-option-container${option.visibility_condition || this.hideDigitalCardsOptions(option) ? ' hidden' : ''}`}
              data-option-id={option.id}
              {...this.getOptionShownWhen(option)}>
              {option.name === 'splitter' ?
                this.splitterOption()
                : <div class={{ "s-product-options-option": true, "s-product-options-option-booking": option.type === DisplayType.BOOKING && salla.url.is_page("cart") }} data-option-type={option.type}
                  data-option-required={`${option.required}`}>
                  <label htmlFor={`options[${option.id}]`} class={`s-product-options-option-label ${this.hideLabel(option) ? 's-product-options-option-label-hidden' : ''}`}>
                    <b>
                      {option.name}
                      {option.required && <span> * </span>} </b>
                    <small>{option.placeholder}</small>
                  </label>
                  <div class={`s-product-options-option-content ${this.hideLabel(option) || (option.type === DisplayType.BOOKING && salla.url.is_page("cart")) ? 's-product-options-option-content-full-width' : ''}`}>
                    {this.getDisplayForType(option)}
                  </div>
                </div>}
            </div>
          )}
        </salla-conditional-fields>
      </Host>
    );

  }

  private generateUniqueKey(defaultValue: string): string {
    return this.uniqueKey ? `${defaultValue}-${this.uniqueKey}` : defaultValue;
  }
  private fillSelectedOptions() {
    this.selectedOptions = this.optionsData.reduce((acc, opt) => {
      const selectedDetails = opt.details.filter(detail => detail.is_selected);
      const mappedDetails = selectedDetails.map(detail => ({
        ...detail,
        option_id: opt.id
      }));
      return acc.concat(mappedDetails);
    }, []);
  }

  componentDidLoad() {
    if (this.optionsData?.length === 0 && !this.optionsData.some(option => option.type === DisplayType.DONATION)) {
      return;
    }
    const selectedDonationOption = this.optionsData.find(option => option.type === DisplayType.DONATION)?.details.find(detail => detail.is_selected);
    if (!selectedDonationOption) {
      return;
    }
    setTimeout(() => {
      salla.event.emit('product-options::donation-changed', {
        id: this.productId,
        price: selectedDonationOption.additional_price
      });
    }, 1000);
  }
  //@ts-ignore
  private donationOption(option: Option, product: Product) {
    return <div class="s-product-options-donation-wrapper">

      {option.donation?.can_donate ? [
        option.donation ?
          <div key={option.id} class="s-product-options-donation-progress">
            <salla-progress-bar donation={option.donation}>
            </salla-progress-bar>
          </div>
          : '',
        option.details.length ?
          [<h4 key={option.id}>{this.selectAmount}</h4>,
          <div key={option.id} class="s-product-options-donation-options">
            {option.details.map((detail, i) =>
              <div key={option.id} class="s-product-options-donation-options-item">
                <input id={this.generateUniqueKey(`donation-option-${i}`)} type="radio" name="donating_option" checked={detail.is_selected} value={detail.additional_price} onChange={e => this.handleDonationOptions(e, detail, 'option')} />
                <label htmlFor={this.generateUniqueKey(`donation-option-${i}`)}>
                  <span innerHTML={salla.money(detail.name)}></span>
                </label>
              </div>
            )}
            {option.donation?.custom_amount_enabled ?
              <div class="s-product-options-donation-options-item">
                <input id={this.generateUniqueKey("donation-option-custom")} type="radio" name="donating_option" value="custom" onChange={e => this.handleDonationOptions(e, 'custom', 'option')} />
                <label htmlFor={this.generateUniqueKey("donation-option-custom")}>
                  <span> {this.selectDonationAmount} </span>
                </label>
              </div>
              : ''
            }
          </div>] : '',

        <div key={option.id} class={{ "s-product-options-donation-input-group": true, "shown": !option.details.length || (option.details.length && this.isCustomDonation) }}>
          <input
            type="text"
            id="donating-amount"
            name="donation_amount"
            class="s-form-control"
            ref={el => { this.donationInput = el as HTMLInputElement }}
            value={
              option.details.length
                && option.details.some(detail => detail.is_selected)
                ? option.details.find(detail => detail.is_selected).additional_price
                : option.value}
            // required
            placeholder={option.placeholder}
            onInput={e => this.handleDonationOptions(e, 'custom', 'input')}
            onBlur={e => this.changedHandler(e, option)}
            onInvalid={(e) => this.invalidHandler(e, option)}
          />
          {/* value={option.value} */}
          <span class="s-product-options-donation-amount-currency">
            {salla.config.currency(salla.config.get('user.currency_code')).symbol}
          </span>
        </div>
      ] :
        this.getExpireDonationMessage(option)
      }
    </div>
  }

  private fileUploader(option: Option, additions: Record<string, unknown> | null = null) {
    return <salla-file-upload
      {...(additions || {})}
      payload-name="file"
      value={option.value}
      instant-upload={true}
      name={`options[${option.id}]`}
      required={!option.visibility_condition && option.required}
      height="120px"
      onAdded={(e) => this.changedHandler(e, option)}
      url={salla.cart.api.getUploadImageEndpoint()}
      form-data={{ cart_item_id: this.productId, product_id: this.productId }}
      onInvalidInput={(e) => this.invalidHandler(e, option)}
      class={{ "s-product-options-image-input": true, required: option.required }}
    >
      <div class="s-product-options-filepond-placeholder">
        <span class="s-product-options-filepond-placeholder-icon"
          innerHTML={(additions as { accept: string }).accept?.split(',').every(type => type.includes('image'))
            ? CameraIcon
            : FileIcon}
        />
        <p class="s-product-options-filepond-placeholder-text">{salla.lang.get('common.uploader.drag_and_drop')}</p>
        <span class="filepond--label-action">{salla.lang.get('common.uploader.browse')}</span>
      </div>
    </salla-file-upload>
  }

  //@ts-ignore
  private imageOption(option: Option) {
    return this.fileUploader(option, { accept: 'image/png,image/jpeg,image/jpg,image/gif' });
  }

  //@ts-ignore
  private fileOption(option: Option) {
    const types = option.details.map(detail => this.fileTypes[detail.name]).filter(Boolean);
    return types?.length
      ? this.fileUploader(option, { accept: types.join(',') })
      : 'File types not selected.';
  }

  // TODO: (ONLY FOR TESTING!) find a better way to make it testable, e.g. wrap it with a unique class like textOption
  //@ts-ignore
  private numberOption(option: Option) {
    return <input
      type="text"
      value={option.value}
      class="s-form-control"
      required={!option.visibility_condition && option.required}
      name={`options[${option.id}]`}
      placeholder={option.placeholder}
      onBlur={e => this.changedHandler(e, option)}
      onInvalid={(e) => this.invalidHandler(e, option)}
      onInput={e => salla.helpers.inputDigitsOnly(e.target)} />
  }

  //@ts-ignore
  private splitterOption() {
    return <div class="s-product-options-splitter" />
  }

  //@ts-ignore
  private textOption(option: Option) {
    return <div class="s-product-options-text">
      <input
        type="text"
        value={option.value}
        maxLength={option?.length}
        class='s-form-control'
        required={!option.visibility_condition && option.required}
        name={`options[${option.id}]`}
        placeholder={option.placeholder}
        onInvalid={(e) => this.invalidHandler(e, option)}
        onChange={e => this.changedHandler(e, option)} />
    </div>
  }

  //@ts-ignore
  private textareaOption(option: Option) {
    //todo::remove mt-1 class, and if it's okay to remove the tag itself will be great
    return <div class="s-product-options-textarea">
      <div class="mt-1">
        <textarea
          rows={4}
          value={option.value}
          maxLength={option?.length}
          class="s-form-control"
          required={!option.visibility_condition && option.required}
          id={`options[${option.id}]`}
          name={`options[${option.id}]`}
          placeholder={option.placeholder}
          onInvalid={(e) => this.invalidHandler(e, option)}
          onChange={(e) => this.changedHandler(e, option)} />
      </div>
    </div>
  }

  //@ts-ignore
  private mapOption(option: Option) {
    return <salla-map
      zoom={15}
      lat={this.getLatLng(option.value, 'lat')}
      lng={this.getLatLng(option.value, 'lng')}
      name={`options[${option.id}]`}
      searchable={true}
      required={option.required}
      onInvalidInput={(e) => this.invalidHandler(e, option)}
      onSelected={e => this.changedHandler(e, option)} />
  }

  private colorPickerOption(option: Option) {
    return <salla-color-picker
      onSubmitted={e => this.changedHandler(e, option)}
      name={`options[${option.id}]`}
      required={!option.visibility_condition && option.required}
      onInvalidInput={(e) => this.invalidHandler(e, option)}
      color={option.value} />
  }

  /**
   * ============= Date Time options =============
   */
  //@ts-ignore
  private timeOption(option: Option) {
    return <salla-datetime-picker
      noCalendar={true}
      enableTime={true}
      dateFormat="h:i K"
      value={option.value}
      placeholder={option.name}
      required={!option.visibility_condition && option.required}
      name={`options[${option.id}]`}
      class="s-product-options-time-element"
      onInvalidInput={(e) => this.invalidHandler(e, option)}
      onPicked={e => this.changedHandler(e, option)} />
  }

  //@ts-ignore
  private dateOption(option: Option) {
    //todo:: consider date-range @see https://github.com/SallaApp/theme-raed/blob/master/src/assets/js/partials/product-options.js#L8-L23
    return <div class="s-product-options-date-element">
      <salla-datetime-picker
        value={option.value}
        placeholder={option.name}
        required={!option.visibility_condition && option.required}
        minDate={new Date()}
        name={`options[${option.id}]`}
        onInvalidInput={(e) => this.invalidHandler(e, option)}
        onPicked={e => this.changedHandler(e, option)} />
    </div>
  }

  //@ts-ignore
  private datetimeOption(option: Option) {
    //todo:: consider date-range @see https://github.com/SallaApp/theme-raed/blob/master/src/assets/js/partials/product-options.js#L8-L23
    return <div class="s-product-options-datetime-element">
      <salla-datetime-picker
        enableTime={true}
        value={option.value}
        dateFormat="Y-m-d G:i:K"
        placeholder={option.name}
        required={!option.visibility_condition && option.required}
        name={`options[${option.id}]`}
        maxDate={option.to_date_time}
        minDate={option.from_date_time}
        onInvalidInput={(e) => this.invalidHandler(e, option)}
        onPicked={e => this.changedHandler(e, option)} />
    </div>
  }

  /**
   * ============= Advanced options =============
   */
  protected getOptionDetailName(detail: Detail, outOfStock = true, optionType?: string) {
    let detailName;
    if (optionType && optionType === DisplayType.COLOR) {
      detailName = detail.name
        + ((outOfStock && this.isOptionDetailOut(detail) && !salla.url.is_page("cart")) && !this.hideOutLabel ? ` <br/> <p> ${this.outOfStockText} </p>` : '')
        + (detail.additional_price ? ` <p> (${salla.money(detail.additional_price, false)}) </p>` : '');
    }

    if(!detailName){
        detailName = detail.name
      + ((outOfStock && this.isOptionDetailOut(detail) && !salla.url.is_page("cart")) && !this.hideOutLabel ? ` - ${this.outOfStockText}` : '')
      + (detail.additional_price ? ` (${salla.money(detail.additional_price, false)})` : '');
    }

    //Some merchants adding price to the names of the options,
    //and because we are using this inside select option, we need to replace the html currency symbol with the store currency symbol
    return detailName.replace('<i class=sicon-sar></i>',salla.config.currency()?.symbol || 'ر.س');
  }


  protected isOptionDetailOut(detail: Detail) {
    if (detail.is_out || !detail.skus_availability || !this.selectedSkus?.length) {
      return detail.is_out;
    }

    const isDetailSelected = this.selectedOptions.filter(option => option.id === detail.id).length;
    //if the current options is the only selected option, so we are sure that it's not out, because there is no other options selected yet
    if (isDetailSelected && this.selectedOptions.length === 1) {
      return false;
    }

    //if current details has sku in the possible outSkus it's out for sure
    if (isDetailSelected) {
      //here we will get the possible outSkus for current selected options
      const outSelectableSkus = this.selectedSkus.filter(sku => this.outSkus.includes(sku));
      return Object.keys(detail.skus_availability).some(sku => outSelectableSkus.includes(Number(sku)))
    }

    return this.selectedOptions.some(option => option.is_out && option.option_id !== detail.option_id)

  }

  /**
   * Renders a single input element (radio or checkbox) for an option detail.
   * @param type - The type of input element ('radio' or 'checkbox').
   * @param detail - The detail object representing an option detail.
   * @param option - The parent option object containing the details.
   * @param isRequired - Indicates if the input is required based on the option's rules.
   * @param name - The name attribute for the input element.
   * @returns HTMLElement - A labeled input element.
   */
  private renderInput(
    type: 'radio' | 'checkbox',
    detail: Detail,
    option: Option,
    isRequired: boolean,
    name: string,
    buttonStyle: boolean,
  ): HTMLElement {
    const id = this.generateUniqueKey(`${type}-${option.id}-${detail.id}`);
    const isDisabled = this.isOptionDetailOut(detail);
    return (
      <label class={{
        "s-product-options-disabled": isDisabled,
      }}>
        <input
          id={id}
          type={type}
          name={name}
          value={detail.id}
          disabled={isDisabled}
          required={isRequired}
          checked={detail.is_selected}
          onInvalid={(e) => this.invalidHandler(e, option)}
          onChange={(e) => this.changedHandler(e, option)}
        />
        <div class={{"s-product-options-grid-mode-span": buttonStyle, "s-product-options-disabled": isDisabled}}>
          {this.getOptionDetailName(detail)}
        </div>
      </label>
    );
  }

  /**
   * Renders a collection of input elements for all details of an option.
   * @param type - The type of input elements ('radio' or 'checkbox').
   * @param option - The parent option object containing the details.
   * @param isRequired - Indicates if the inputs are required based on the option's rules.
   * @returns HTMLElement[] - An array of labeled input elements.
   */
  private renderOptionDetails(
    type: 'radio' | 'checkbox',
    option: Option,
    isRequired: boolean,
    buttonStyle: boolean = false
  ): HTMLElement[] {
    const name = type === 'radio' ? `options[${option.id}]` : `options[${option.id}][]`;
    return option?.details.map((detail: Detail) =>
      this.renderInput(type, detail, option, isRequired, name, buttonStyle)
    );
  }

  /**
   * Renders a dropdown (select) element for a single-option selection.
   * @param option - The parent option object.
   * @returns HTMLElement - A select dropdown element with all option details.
   */
  private renderSelect(option: Option): HTMLElement {
    return (
      <div>
        <select
          name={`options[${option.id}]`}
          required={!option.visibility_condition && option.required}
          class="s-form-control"
          onInvalid={(e) => this.invalidHandler(e, option)}
          onChange={(e) => this.changedHandler(e, option)}
        >
          <option value="">{option.placeholder}</option>
          {option?.details.map((detail: Detail) => (
            <option
              key={detail.id}
              value={detail.id}
              disabled={this.canDisabled && this.isOptionDetailOut(detail)}
              selected={detail.is_selected}
            >
              {this.getOptionDetailName(detail)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  /**
   * Renders a grid-based layout for option inputs (radio or checkbox).
   * @param type - The type of input elements ('radio' or 'checkbox').
   * @param option - The parent option object containing the details.
   * @param isRequired - Indicates if the inputs are required based on the option's rules.
   * @returns HTMLElement - A grid-based container with input elements.
   */
  private renderButtonStyle(
    type: 'radio' | 'checkbox',
    option: Option,
    isRequired: boolean
  ): HTMLElement {
    return (
      <div class="s-product-options-grid-mode">
        {this.renderOptionDetails(type, option, isRequired, true)}
      </div>
    );
  }

  /**
   * Renders a single-option selection, either as a grid or dropdown, based on configuration.
   * @param option - The parent option object.
   * @returns HTMLElement - The rendered single-option element.
   */
  private singleOption(option: Option): HTMLElement {
    const buttonStyle = this.optionConfig?.['single-option']?.type === 'button';
    const isRequired = !option.visibility_condition && option.required;

    return buttonStyle
      ? this.renderButtonStyle('radio', option, isRequired)
      : this.renderSelect(option);
  }

  /**
   * Renders a multiple-option selection, either as a grid or list, based on configuration.
   * @param option - The parent option object.
   * @returns HTMLElement - The rendered multiple-option element.
   */
  private multipleOptions(option: Option): HTMLElement {
    const buttonStyle = this.optionConfig?.['multiple-option']?.type === 'button';
    const isRequired =
      option.required &&
      !option.details.some((detail) => detail.is_selected) &&
      !option.visibility_condition;

    return buttonStyle
      ? this.renderButtonStyle('checkbox', option, isRequired)
      : (
        <div
          class={{
            's-product-options-multiple-options-wrapper': true,
            required: option.required,
          }}
        >
          {this.renderOptionDetails('checkbox', option, isRequired)}
        </div>
      );
  }
  
  //@ts-ignore
  private colorOption(option: Option) {
    return (
      <fieldset
        class="s-product-options-colors-wrapper"
      >
        {option?.details.map((detail) => (
          <div
            class="s-product-options-colors-item"
            key={detail.id}
          >
            <input
              type="radio"
              value={detail.id}
              required={!option.visibility_condition && option.required}
              checked={detail.is_selected}
              name={`options[${option.id}]`}
              disabled={this.canDisabled && this.isOptionDetailOut(detail)}
              id={this.generateUniqueKey(`color-${this.productId}-${option.id}-${detail.id}`)}
              onInvalid={(e) => this.invalidHandler(e, option)}
              onChange={(e) => this.changedHandler(e, option)}
            />
            <label htmlFor={this.generateUniqueKey(`color-${this.productId}-${option.id}-${detail.id}`)}>
              <span style={{ backgroundColor: detail.color }} />
              <div innerHTML={this.getOptionDetailName(detail, true, option.type)} />
            </label>
          </div>
        ))}
      </fieldset>
    );
  }  

  //@ts-ignore
  private thumbnailOption(option: Option) {
    return <div class="s-product-options-thumbnails-wrapper">
      {option.details.map((detail: Detail) => {
        return <div key={detail.id}>
          <input type="radio"
            value={detail.id}
            data-itemid={detail.id} //todo:: why need this? it's already in the value!
            required={!option.visibility_condition && option.required}
            checked={detail.is_selected}
            name={`options[${option.id}]`}
            data-img-id={detail.option_value}
            disabled={this.canDisabled && this.isOptionDetailOut(detail)}
            id={this.generateUniqueKey(`option_${this.productId}-${option.id}_${detail.id}`)}
            onInvalid={(e) => this.invalidHandler(e, option)}
            onChange={(e) => this.changedHandler(e, option)} />
          <label htmlFor={this.generateUniqueKey(`option_${this.productId}-${option.id}_${detail.id}`)}
            data-img-id={detail.option_value}
            class="go-to-slide">
            <img data-src={detail.image} src={detail.image} title={detail.name} alt={detail.name} />
            <span innerHTML={CheckCircleIcon} class="s-product-options-thumbnails-icon" />
            {this.isOptionDetailOut(detail) ?
              [
                <small key={detail.id} class="s-product-options-thumbnails-stock-badge">{this.outOfStockText}</small>,
                this.canDisabled ? <div key={detail.id} class="s-product-options-thumbnails-badge-overlay" /> : '',
              ]
              : ''}
          </label>
          <p>{this.getOptionDetailName(detail, false)} </p>
        </div>
      })}
    </div>
  }

  // Digital card options
  private digitalCardValuesOption(option: Option) {

    return <div class="s-product-options-digital-card-wrapper">
      {this.availableDigitalCardValues.length > 0 ? this.availableDigitalCardValues.map((detail) => {
        const id = String(detail.id)
        return <label htmlFor={this.generateUniqueKey(id.toString())} key={id} class="s-product-options-digital-card-option">
          <input type="radio" data-code-value class="s-form-control s-product-options-digital-card-input" value={detail.id}
            name={`options[${option.id}]`}
            id={this.generateUniqueKey(id.toString())}
            required={!option.visibility_condition && option.required}
            onInvalid={(e) => this.invalidHandler(e, option)}
            {...(!this.ignoreDefaultCardValue ? {defaultChecked: this.getSelectedDigitalCardOptions(option)?.id === detail.id}:{})}

          />
          <span>{detail.name} {salla.config?.currency()?.symbol}</span>
        </label>
      })
        : <div class="s-product-options-digital-card-out-of-stock"/>
      }
    </div>
  }
  private countryOption(option: Option) {
    return <div class="s-product-options-digital-card-wrapper">
      {option.details.map((detail) => {

       return <label htmlFor={this.generateUniqueKey(detail.id.toString())} key={detail.id} class={{"s-product-options-digital-card-option":true,"s-product-options-digital-card-option-stock-out":detail.is_out}}>
          <input id={this.generateUniqueKey(detail.id.toString())} type="radio" class="s-form-control s-product-options-digital-card-input" value={detail.id}
            name={`options[${option.id}]`}
            disabled={detail.is_out}
            required={!option.visibility_condition && option.required}
            onInvalid={(e) => this.invalidHandler(e, option)}
            onChange={e => this.changedHandler(e, option)}
            onClick={()=>{ this.disableCardValue = false}}
            {...(salla.url.is_page("cart") ? {defaultChecked:this.getSelectedDigitalCardOptions(option)?.id === detail.id}:{})}

          />
               <img loading='lazy' alt={detail.code} height={24} width={24} class="s-product-options-country-flag" src={`https://cdn.assets.salla.network/prod/admin/cp/assets/flags/1x1/${String(detail.code).toLocaleLowerCase()}.svg`}/>
          <span >{detail.name}</span>
        </label>
  })
      }
    </div>

  }

}